import React, { useState, useEffect } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Formik } from 'formik';
import TextArea from '../../../BaseComponents/TextArea';
import './RecommendationsModal.scss';
import Button from '../../../BaseComponents/Button';
import TextInput from '../../../BaseComponents/TextInput';
import { CreateRecommendationRequest, RecommendationCategory } from '../../../../types/apiGeneratedTypes';
import {
  createRecommendation,
  updateRecommendation,
  getRecommendationCategories,
  getRecommendationsList
} from '../../../../api/recommendation';
import {
  changeDeleteRecommendationModalVisibility,
  setRecommendationToDelete,
  changeModalVisibility,
  setRecommendationsList
} from '../../../../store/recommendationSlice';
import { ReactComponent as BinIcon } from '../../../../assets/icons/TrashBin2.svg';
import UploadPhoto from '../../../UploadPhoto';
import { toast } from 'react-toastify';
import Select from '../../../BaseComponents/Select';

type RecommendationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

interface RecommendationFormValues {
  title: string;
  description: string;
  photo: string;
  category_id: number;
}

const RecommendationsModal: React.FC<RecommendationsModalProps> = ({
  isOpen,
  onClose
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [categories, setCategories] = useState<RecommendationCategory[]>([]);
  const { recommendationToEdit } = useSelector(
    (state: RootState) => state.recommendations
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getRecommendationCategories();
        setCategories(response.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Error loading categories');
      }
    };
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const getDefaultValues = (): RecommendationFormValues => {
    if (recommendationToEdit) {
      const recommendationData = recommendationToEdit;
      if (recommendationData) {
        return {
          title: recommendationData.title ?? '',
          description: recommendationData.description ?? '',
          photo: recommendationData.image ?? '',
          category_id: recommendationData.category?.id ?? 0
        };
      }
    }
    return {
      title: '',
      description: '',
      photo: '',
      category_id: 0
    };
  };

  const defaultValues = getDefaultValues();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => dispatch(changeModalVisibility(false))}
      title={'Add Recommendation'}
      width={'900px'}
    >
      <Formik
        initialValues={defaultValues}
        onSubmit={async (values) => {
          setIsLoading(true);
          try {
            const requestObject: CreateRecommendationRequest = {
              title: values.title,
              description: values.description,
              image: values.photo,
              category_id: values.category_id
            };
            if (recommendationToEdit) {
              await updateRecommendation({
                ...requestObject,
                id: recommendationToEdit?.id as number
              });
              toast.success('Recommendation updated successfully');
            } else {
              await createRecommendation(requestObject);
              toast.success('Recommendation added successfully');
            }
            dispatch(changeModalVisibility(false));
            const updatedList = await getRecommendationsList(0, 1000);
            if (updatedList) {
              dispatch(setRecommendationsList(updatedList));
            }
            onClose();
          } catch (error) {
            setError(error);
            toast.error('Error adding or updating recommendation');
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} className='recommendation-modal-form'>
            <div className='upload-photo-wrapper'>
              <UploadPhoto
                defaultPhoto={recommendationToEdit?.image_url || values.photo}
                onPhotoChange={(photo) => setFieldValue('image', photo)}
              />
            </div>
            <Select
              name='category_id'
              title='Category'
              selectedOption={{ 
                key: values.category_id, 
                value: categories.find(c => c.id === values.category_id)?.name || '' 
              }}
              onChange={(option) => setFieldValue('category_id', option.key)}
              options={categories.map(category => ({
                key: category.id || 0,
                value: category.name
              }))}
              placeholder='Select a category'
              isError={false}
            />
            <TextInput
              name='title'
              title='Title *'
              placeholder='Enter title'
              value={values.title}
              onChange={(e) => setFieldValue('title', e.target.value)}
              isError={false}
            />
            <TextArea
              name='description'
              title='Description *'
              placeholder='Enter description'
              value={values.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              isError={false}
            />
            <div className='btn-wrapper'>
              <Button
                text={
                  recommendationToEdit ? 'Save changes' : 'Add Recommendation'
                }
                type='submit'
                isDisabled={
                  !values.title || !values.description || !values.photo || !values.category_id
                }
                isLoading={isLoading}
              />
              {recommendationToEdit && (
                <div
                  className='delete-btn'
                  onClick={() => {
                    dispatch(setRecommendationToDelete(recommendationToEdit));
                    dispatch(changeDeleteRecommendationModalVisibility(true));
                  }}
                >
                  <BinIcon />
                  Delete Recommendation
                </div>
              )}
              {error && (
                <div className='error-message'>
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default RecommendationsModal;
